/**
 * Client-side clock-sync coordinator. Drives N rounds of
 * clock_sync_request/response, picks the best offset, and exposes
 * `serverNow()` for the rest of the app.
 *
 * The math (computeSample, reduceSamples) lives in the worker package because
 * it's the same on both sides; here we just import and apply it.
 */
import {
  CLOCK_SYNC_ROUND_INTERVAL_MS,
  CLOCK_SYNC_ROUNDS_INITIAL,
  CLOCK_SYNC_ROUNDS_REFRESH,
  CLOCK_SYNC_REFRESH_INTERVAL_MS,
} from '@syncstream/protocol';

interface ClockSample {
  rttMs: number;
  offsetMs: number;
}

function computeSample(
  clientSendTime: number,
  serverTime: number,
  clientRecvTime: number,
): ClockSample {
  const rttMs = clientRecvTime - clientSendTime;
  const offsetMs = serverTime - clientSendTime - rttMs / 2;
  return { rttMs, offsetMs };
}

function reduceSamples(samples: ClockSample[]): number | null {
  if (samples.length === 0) return null;
  const sortedByRtt = [...samples].sort((a, b) => a.rttMs - b.rttMs);
  const dropCount = Math.min(
    sortedByRtt.length - 1,
    Math.max(0, Math.floor(sortedByRtt.length * 0.25)),
  );
  const kept = sortedByRtt.slice(0, sortedByRtt.length - dropCount);
  if (kept.length === 0) return null;
  const offsets = kept.map((s) => s.offsetMs).sort((a, b) => a - b);
  const mid = Math.floor(offsets.length / 2);
  if (offsets.length % 2 === 1) return offsets[mid] ?? null;
  const a = offsets[mid - 1];
  const b = offsets[mid];
  if (a == null || b == null) return null;
  return (a + b) / 2;
}

export class ClockSync {
  private offsetMs = 0;
  private samples: ClockSample[] = [];
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private inFlight = new Map<number, () => void>();

  constructor(
    private readonly send: (clientSendTime: number) => void,
    private readonly onUpdate?: (offsetMs: number) => void,
  ) {}

  /** Call this with the client's send time after every clock_sync_response. */
  recordResponse(clientSendTime: number, serverTime: number): void {
    const now = Date.now();
    this.samples.push(computeSample(clientSendTime, serverTime, now));
    const resolve = this.inFlight.get(clientSendTime);
    if (resolve) {
      this.inFlight.delete(clientSendTime);
      resolve();
    }
  }

  /** Server clock estimate. */
  serverNow(): number {
    return Date.now() + this.offsetMs;
  }

  getOffset(): number {
    return this.offsetMs;
  }

  /** Run the initial batch (8 rounds). Resolve when finished. */
  async runInitial(): Promise<void> {
    await this.runBatch(CLOCK_SYNC_ROUNDS_INITIAL);
    this.finalize();
    this.scheduleRefresh();
  }

  private scheduleRefresh(): void {
    if (this.refreshTimer != null) clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(async () => {
      await this.runBatch(CLOCK_SYNC_ROUNDS_REFRESH);
      this.finalize();
      this.scheduleRefresh();
    }, CLOCK_SYNC_REFRESH_INTERVAL_MS);
  }

  private async runBatch(rounds: number): Promise<void> {
    this.samples = [];
    for (let i = 0; i < rounds; i++) {
      const t = Date.now();
      const done = new Promise<void>((resolve) => {
        this.inFlight.set(t, resolve);
      });
      this.send(t);
      await Promise.race([
        done,
        new Promise<void>((r) => setTimeout(r, 2000)), // skip stalled round
      ]);
      this.inFlight.delete(t);
      if (i < rounds - 1) await sleep(CLOCK_SYNC_ROUND_INTERVAL_MS);
    }
  }

  private finalize(): void {
    const o = reduceSamples(this.samples);
    if (o != null) {
      this.offsetMs = o;
      this.onUpdate?.(o);
    }
  }

  stop(): void {
    if (this.refreshTimer != null) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}
